﻿<script lang="ts" setup>
import { ref, nextTick, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import AuthenticateLayout from 'src/components/layouts/AuthenticateLayout.vue';
import ProgressBar from 'src/components/ProgressBar.vue';
import { createProfileInformation, getRegistrationNextStepSkipLoginWithExting } from 'src/api/endpoints/registrationWeb';
import { handleApiRequest } from 'src/api/handleApiRequest';
import { translateStepToRoute } from 'src/utils/interfaceUtils';
import { useSessionStore } from 'src/store';
import { useQuasar } from 'quasar';
import {useI18n} from 'vue-i18n';

const supportUrlOrProfileInfo = computed(() =>  t('activate_auth_help_click_here_pwe'));

const router = useRouter(),
  {t} = useI18n(),
  postalCode = ref(''),
  postalCodeInput = ref({} as any),
  email = ref(''),
  emailInput = ref({} as any),
  formIsValid = ref(false),
  sessionModule = useSessionStore(),
  $q = useQuasar();
  const showCustomerSupportInfoDialog = ref(false);

function validate() {
  nextTick(() => {
    formIsValid.value = postalCodeInput.value?.validate() && emailInput.value?.validate();
  });
}

async function onNextClick() {
  if (!formIsValid.value) return;
  $q.loading.show()
  const response = await handleApiRequest(() =>
    createProfileInformation(email.value, postalCode.value), null, $q
  );
  if (response?.result) {
    const step = await handleApiRequest(() => getRegistrationNextStepSkipLoginWithExting(), null, $q);
    const nextStep = translateStepToRoute(step?.result as string);
    $q.loading.hide()
    router.push({
      name: nextStep,
    });
  } else {
    $q.loading.hide()
  }
}

onMounted(() => {
  sessionModule.setIsHeaderVisible(true);
  (document.querySelectorAll('input:first-child')[0] as HTMLElement).focus();
});
</script>

<template>
  <q-page padding>
    <AuthenticateLayout withHeader>

      <!-- Heading -->
      <template #heading>
        <div class="row no-wrap items-center">
          <div class="col">
            {{ t('activate_profile_title') }}
          </div>
          <div class="col-auto">
            <zbm-btn-icon @click="showCustomerSupportInfoDialog = true"
                          icon="fa-light fa-circle-question"
                          size="30px"
                          data-testid="help" />
          </div>
        </div>
      </template>

      <!-- View Unique Content -->
      <template #default>

        <zbm-dialog v-model="showCustomerSupportInfoDialog">
          <div class="row q-dialog__header">
            <div class="col-auto">
              <q-icon color="dark-teal" name="fa-solid fa-circle-question" size="42px" />
            </div>
          </div>
          <div class="row q-dialog__body">
            <div class="col">
              <h3 class="text-weight-semibold q-mb-lg">{{ t('sign_up_help') }}</h3>
              
              <div class="support-info-text" v-html="t('activate_auth_help_mymobility_phone_email_pwe', [
                sessionModule.supportInfo?.countrySupportPhone,
                sessionModule.supportInfo?.countrySupportEmail,
                sessionModule.supportInfo?.patientSupportUrl,supportUrlOrProfileInfo])"
              ></div>
            </div>
          </div>

          <div class="row q-dialog__footer">
            <div class="col-auto">
              <zbm-btn
                v-close-popup
                :color="$zb.enums.ButtonStdColorEnum.accentText"
                :height="$zb.enums.ButtonHeightEnum.lg"
                :label="t('gen_okay')"
                data-testid="okay"
              />
            </div>
          </div>
        </zbm-dialog>

        <!-- Email -->
        <div class="form-row">
          <div class="col-auto" data-testid="email">
            <zbm-input-with-validation v-model="email" v-on:keyup.enter="onNextClick" ref="emailInput" type="email"
                                       @update:modelValue="validate" :placeholder="t('email_address')"
                                       :rules="[$zb.validators.required('activate_profile_email_required'), $zb.validators.email()]" />
          </div>
        </div>

        <!-- Postal Code -->
        <div class="form-row">
          <div class="col-auto" data-testid="postalCode">
            <zbm-input-with-validation v-model="postalCode" v-on:keyup.enter="onNextClick" ref="postalCodeInput"
                                       type="text" @update:modelValue="validate" :placeholder="t('activate_profile_postal_code')"
                                       :rules="[$zb.validators.maxLen(25)]" />
          </div>
        </div>
      </template>

      <!-- Progress -->
      <template v-slot:actions>
        <ProgressBar :backEnabled="false" :nextRoute="'Dashboard.Home'" :progress="100"
                     v-model:nextEnabled="formIsValid" @on-next-click="onNextClick" />
      </template>
    </AuthenticateLayout>
  </q-page>
</template>

<style lang="scss" scoped>
:deep(.auth-layout__content) {
  min-height: 400px;
}

.support-info-text {
    word-wrap: break-word;
}
</style>
